/**
 *  Copyright AlpineReplay Inc, 2021. All rights reserved.
 *  Authors: Brian Bal, Leonid Khramov
 */
import { connect } from "react-redux"
import React, { Component } from "react"
import { withRouter } from "react-router"

import passwordValidator from "password-validator"

import "./OwnerSetupContainer.css"
import { PRIVACY_POLICY, TERMS_OF_USE } from "../../../util/links"
import TopBar from "../components/TopBar"
import { webApi } from "../../../lib/WebApi"
import { statAdminEvent } from "../../../util/analytics"
import { fullName2FirstLast } from "../../../util/helpers"

import { TRCButton, TRCInput } from "../../../trace"
import { pageTitle } from "trux"
import generator from "generate-password"

class OwnerSetup extends Component {
    constructor(props) {
        super(props)
        this.state = {
            errors: [],
            editState: { fullName: "", password: "", password2: "" },
            inviteInfo: null,
            teams: [],
        }

        this.onCompleteSetup = this.onCompleteSetup.bind(this)
        this.onPassword2Changed = this.onPassword2Changed.bind(this)
        this.onPasswordChanged = this.onPasswordChanged.bind(this)
        this.onUserFullNameChanged = this.onUserFullNameChanged.bind(this)
        this.validator = new passwordValidator()
        this.validator.is().min(6).has().lowercase()
    }

    componentDidMount() {
        pageTitle("Owner Invitation", "Settings - Owner Invite")
    }

    caption(field, text) {
        const errors = this.state.errors
        if (!field) {
            if (errors.length > 0) return errors[0].error
            return ""
        }
        for (const error of errors) {
            if (error.field === field) {
                return error.error
            }
        }
        return text
    }

    errorClass(field) {
        const errors = this.state.errors
        if (!field && errors.length > 0) return "invalid"
        for (const error of errors) {
            if (error.field === field) {
                return "invalid"
            }
        }
        return ""
    }

    errorWithout(field) {
        let err = []
        for (const item of this.state.errors) {
            if (item.field !== field) {
                err.push(item)
            }
        }
        return err
    }

    componentWillMount() {
        const wthis = this
        webApi.getInviteInfo(this.props.inviteId).then((data) => {
            if (data) {
                let pw = generator.generate({
                    length: 32,
                    numbers: true,
                    symbols: true,
                    lowercase: true,
                    uppercase: true,
                })
                if (data.is_new_user) {
                    wthis.setState({
                        editState: {
                            fullName: "",
                            password: pw,
                            password2: pw,
                        },
                        inviteInfo: data,
                    })
                } else {
                    wthis.setState({
                        editState: {
                            fullName: `${data.first_name} ${data.last_name}`,
                            password: pw,
                            password2: pw,
                        },
                        inviteInfo: data,
                    })
                }
            }
        })
    }

    render() {
        const editState = this.state.editState
        let disabled = false
        if (this.state.inviteInfo && !this.state.inviteInfo.is_new_user) {
            disabled = true
        }

        return (
            <div className="OwnerSetup">
                <TopBar hideMenu />

                <div className="trace-container content">
                    <h1>Complete Setup</h1>
                    <span>Please accept your invitation below.</span>

                    <div className="areablock">
                        <h4>Your Full Name</h4>
                        <TRCInput
                            type="text"
                            placeholder="Enter Full Name"
                            value={editState.fullName}
                            onChange={this.onUserFullNameChanged}
                            disabled={disabled}
                            error={this.caption("fullName")}
                        />
                    </div>

                    <div className="password-area">
                        <h4>Your Password</h4>
                        <TRCInput
                            type="password"
                            placeholder="Password"
                            value={editState.password}
                            onChange={this.onPasswordChanged}
                            error={this.caption("password")}
                        />
                        {disabled === false && (
                            <TRCInput
                                type="password"
                                placeholder="Re-enter Password"
                                value={editState.password2}
                                onChange={this.onPassword2Changed}
                                error={this.caption("password")}
                            />
                        )}
                    </div>

                    <div className="bottom-buttons">
                        <TRCButton full onClick={this.onCompleteSetup}>
                            Accept
                        </TRCButton>
                    </div>

                    <span className="subscript">
                        By signing up you agree to our{" "}
                        <a href={TERMS_OF_USE}>Terms of Service</a>
                        and <a href={PRIVACY_POLICY}>Privacy Policy</a>
                    </span>
                </div>
            </div>
        )
    }

    onUserFullNameChanged(e) {
        //const editState = Object.assign({}, this.state.editState, {fullName: e.target.value});
        const editState = Object.assign({}, this.state.editState, {
            fullName: e,
        })
        this.setState({
            editState: editState,
            errors: this.errorWithout("fullName"),
        })
    }

    onPasswordChanged(e) {
        //const editState = Object.assign({}, this.state.editState, {password: e.target.value});
        const editState = Object.assign({}, this.state.editState, {
            password: e,
        })
        if (!this.state.inviteInfo.is_new_user) {
            editState.password2 = editState.password
        }
        this.setState({
            editState: editState,
            errors: this.errorWithout("password"),
        })
    }

    onPassword2Changed(e) {
        //const editState = Object.assign({}, this.state.editState, {password2: e.target.value});
        const editState = Object.assign({}, this.state.editState, {
            password2: e,
        })
        this.setState({
            editState: editState,
            errors: this.errorWithout("password2"),
        })
    }

    onCompleteSetup(e) {
        e.preventDefault()
        const state = this.state.editState
        const ret = this.validator.validate(state.password, { list: true })
        console.log("Password validation: " + ret)

        if (state.fullName.trim().length < 4) {
            this.setState({
                errors: [
                    {
                        field: "fullName",
                        error: "Name is too short, please enter full name",
                    },
                ],
            })
            return
        }

        let data = {
            invite_code: this.props.inviteCode,
            password: state.password,
        }
        if (this.state.inviteInfo.is_new_user) {
            data = Object.assign(data, fullName2FirstLast(state.fullName))
        }

        webApi
            .acceptInvite(this.props.inviteId, data)
            .then(() => {
                return webApi._get("/webapp/users/self").then((user) => {
                    console.log(user)
                })
            })
            .then(() => {
                statAdminEvent(this.props.divisionId, "accept-invite", "owner")
                this.props.reload()
                this.props.history.replace(
                    `/tbd/settings/division/${this.props.divisionId}/teams`
                )
            })
            .catch((e) => console.error(e))
    }
}

const mapStateToProps = (state, props) => {
    const divisionId = props.match.params.division_id
    const inviteId = props.match.params.invite_id
    const inviteCode = props.match.params.invite_code
    const userId = props.match.params.user_id
    return {
        divisionId,
        inviteId,
        inviteCode,
        userId,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        reload: () => {
            dispatch({
                type: "COMMON_RESET",
            })
        },
    }
}

const OwnerSetupContainer = withRouter(
    connect(mapStateToProps, mapDispatchToProps)(OwnerSetup)
)

export default OwnerSetupContainer
